import { Pagination, Table, type TablePaginationConfig } from 'antd';
import { Button } from 'antd';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import type { FixedType } from 'rc-table/lib/interface';
import type { Dispatch, SetStateAction } from 'react';
import { DASHBOARD_DIV_ID, loanConfig, serviceErrorCodes } from '~/constants';
import {
  openServicingDashboard,
  resumeApplication,
} from '~/services/SdkService';
import { fetchLoans } from '~/services/loanService';
import { useLoansStore } from '~/store/loans';
import type { SdkError } from '~/types/interaction';
import type { LoanFiltersType, LoanRecord } from '~/types/loans';
import { defaultFilters, filtersKey } from '~/utils/constants';
import notify from '~/utils/useNotification';
import { isDateRangeWithinLimit } from '~/utils/utilities';
import { loanUserColumns } from './LoanUserTableConfig';

interface LoanUserTableProps {
  filters: LoanFiltersType;
  setFilters: Dispatch<SetStateAction<LoanFiltersType>>;
  setToggleDashboard: Dispatch<SetStateAction<boolean>>;
  /*Used to show the action column only for mfd partner*/
  showActionColumn: boolean;
  /*Used to check if the parter selected in filters is a mfd partner*/
  isMfdPartner: boolean;
}

const ctaLabels: Record<string, string> = {
  VIEW_DASHBOARD: 'View Dashboard',
  RESUME_APPLICATION: 'Resume',
};

const LoanUserTable = ({
  filters = defaultFilters,
  setFilters,
  setToggleDashboard,
  showActionColumn,
  isMfdPartner,
}: LoanUserTableProps) => {
  const { loans, loading, totalItems } = useLoansStore();

  const ctaHandlers: Record<string, (record: LoanRecord) => Promise<void>> = {
    VIEW_DASHBOARD: async (record) => {
      setToggleDashboard(true);
      await openDashboard(record);
    },
    RESUME_APPLICATION: async (record) => {
      const [ok, result] = await resumeApplication(
        'APPLY',
        record.unityUserId,
      );
      if (!ok) {
        notify({
          type: 'error',
          title: 'Gateway SDK returned an error',
          description: (result as SdkError).message,
        });
      }
    },
  };

  const handlePaginationChange = async (page: number, pageSize?: number) => {
    const updatedFilters = {
      ...filters,
      page,
      limit: pageSize || filters.limit,
    };

    if (
      !isDateRangeWithinLimit(
        updatedFilters.fromDate,
        updatedFilters.toDate,
        loanConfig.maxLoanFilterDateRangeInMonths,
      )
    ) {
      notify({
        type: 'warning',
        title: 'Date range exceeded',
        description: `Date range should not exceed ${loanConfig.maxLoanFilterDateRangeInMonths} months`,
      });
      return;
    }
    /* EdgeCase:- Earlier it was updating the filters with the new page number irrespective of the success of the fetchLoans() so added this to encounter that thing.  */
    const fetchSuccessful = await fetchLoans(updatedFilters);
    if (fetchSuccessful) {
      setFilters(updatedFilters);
      sessionStorage.setItem(filtersKey, JSON.stringify(updatedFilters));
    }
  };

  const getFooter = () => (
    <Pagination
      current={filters.page}
      pageSize={filters.limit}
      onChange={handlePaginationChange}
      total={totalItems}
      showSizeChanger={true}
      pageSizeOptions={['10', '20']}
      onShowSizeChange={handlePaginationChange}
    />
  );

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filts: Record<string, FilterValue | null>,
    sorter: SorterResult<LoanRecord> | SorterResult<LoanRecord>[],
  ) => {
    if (sorter && !Array.isArray(sorter) && 'field' in sorter) {
      const sortField = sorter.field as string;
      const sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
      let updatedFilters = { ...filters };

      switch (sortField) {
        case 'journeyStatus':
          updatedFilters = {
            ...updatedFilters,
            sortByjourneyStatus: sortOrder,
            sortByLoanStartedAt: '',
            sortByLoanModifiedAt: '',
            page: 1,
          };
          break;
        case 'createdAt':
          updatedFilters = {
            ...updatedFilters,
            sortByLoanStartedAt: sortOrder,
            sortByjourneyStatus: '',
            sortByLoanModifiedAt: '',
            page: 1,
          };
          break;
        case 'updatedAt':
          updatedFilters = {
            ...updatedFilters,
            sortByLoanModifiedAt: sortOrder,
            sortByjourneyStatus: '',
            sortByLoanStartedAt: '',
            page: 1,
          };
          break;
        default:
          break;
      }
      setFilters(updatedFilters);
      sessionStorage.setItem(filtersKey, JSON.stringify(updatedFilters));
      fetchLoans(updatedFilters);
    }
  };

  //open the servicing dashboard in connected mode for the user
  const openDashboard = async (record: LoanRecord) => {
    const [ok, result] = await openServicingDashboard({
      userId: record.unityUserId,
      lId: record.lid,
      target: DASHBOARD_DIV_ID,
      onTopupTriggered: async (userId) => {
        const [ok, result] = await resumeApplication('TOP_UP', userId);
        if (!ok) {
          setToggleDashboard(false);
          notify({
            type: 'error',
            title: 'Gateway SDK returned an error',
            description: (result as SdkError).message,
          });
          return;
        }
      },
    });
    if (!ok) {
      if ((result as SdkError)?.code !== serviceErrorCodes.TOPUP_TRIGGERED) {
        setToggleDashboard(false);
        notify({
          type: 'error',
          title: 'Gateway SDK returned an error',
          description: (result as SdkError).message,
        });
        return;
      }
    }
  };

  //filter out the action column for non mfd partners
  const columns = loanUserColumns
    .filter((col) => col.key !== 'action' || (showActionColumn && isMfdPartner))
    .map((col) => {
      if (col.key === 'action') {
        return {
          ...col,
          fixed: 'right' as FixedType,
          render: (_: unknown, record: LoanRecord) => {
            return record.cta !== 'NO_CTA' ? (
              <Button
                type="default"
                className="rounded-none w-[150px]"
                onClick={() => ctaHandlers[record.cta](record)}
              >
                {ctaLabels[record.cta]}
              </Button>
            ) : (
              <></>
            );
          },
        };
      }
      return col;
    });

  return (
    <Table
      dataSource={loans}
      rowKey="lid"
      columns={columns}
      pagination={false}
      footer={getFooter}
      onChange={handleTableChange}
      className="w-full"
      loading={loading}
      scroll={{ x: 2750 }}
    />
  );
};

export default LoanUserTable;
